<script setup lang="ts">
import MainLayout from "@/layouts/MainLayout.vue";
import axios from "axios";
import { onMounted, ref, computed } from "vue";
import { useUserStore } from "@/stores/user";

const userStore = useUserStore();
const api = axios.create({
  baseURL: process.env.VUE_APP_API_ENDPOINT + "/api/v1",
  headers: { "Content-Type": "application/json" },
});
api.interceptors.request.use((config) => {
  config.headers["x-supabase-auth"] = userStore.getAccessToken;
  return config;
});

type ReviewStatus = "pending" | "sent" | "positive" | "negative";

interface ReviewRequest {
  id: number;
  project_id: number;
  project_name: string | null;
  customer_id: number;
  customer_name: string | null;
  partner_name: string | null;
  status: ReviewStatus;
  sent_at: string | null;
  responded_at: string | null;
  feedback_text: string | null;
}

const reviews = ref<ReviewRequest[]>([]);
const isLoading = ref(false);
const activeFilter = ref<ReviewStatus | "all">("all");
const expandedFeedback = ref<number | null>(null);

const statusFilters: { key: ReviewStatus | "all"; label: string }[] = [
  { key: "all", label: "Alle" },
  { key: "sent", label: "Keine Antwort" },
  { key: "positive", label: "Positiv" },
  { key: "negative", label: "Negativ" },
];

const filtered = computed(() => {
  if (activeFilter.value === "all") return reviews.value;
  if (activeFilter.value === "sent")
    return reviews.value.filter(
      (r) => r.status === "sent" || r.status === "pending"
    );
  return reviews.value.filter((r) => r.status === activeFilter.value);
});

const counts = computed(() => ({
  all: reviews.value.length,
  sent: reviews.value.filter(
    (r) => r.status === "sent" || r.status === "pending"
  ).length,
  positive: reviews.value.filter((r) => r.status === "positive").length,
  negative: reviews.value.filter((r) => r.status === "negative").length,
}));

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

async function fetchReviews() {
  isLoading.value = true;
  try {
    const { data } = await api.get("/reviews/", { params: { limit: 500 } });
    reviews.value = data.review_requests;
  } catch (e) {
    console.error(e);
  } finally {
    isLoading.value = false;
  }
}

onMounted(fetchReviews);
</script>

<template>
  <MainLayout>
    <div class="w-full h-[calc(100vh-52px)] overflow-y-auto bg-[#F1F0EE]">
      <div class="max-w-[900px] mx-auto px-6 py-8">
        <!-- Header -->
        <div class="mb-6">
          <h1
            class="text-xl font-semibold text-primary-text-color tracking-[0.01rem]"
          >
            Bewertungen
          </h1>
          <p class="text-sm text-[#898790] mt-1">
            Übersicht aller versendeten Bewertungsanfragen
          </p>
        </div>

        <!-- Stat cards -->
        <div class="grid grid-cols-4 gap-3 mb-6">
          <button
            v-for="f in statusFilters"
            :key="f.key"
            @click="activeFilter = f.key"
            class="bg-white rounded-xl px-4 py-3 text-left transition border-2"
            :class="
              activeFilter === f.key
                ? 'border-gray-900'
                : 'border-transparent hover:border-gray-200'
            "
          >
            <div class="text-2xl font-semibold text-gray-900">
              {{ counts[f.key] }}
            </div>
            <div class="text-xs text-[#898790] mt-0.5">{{ f.label }}</div>
          </button>
        </div>

        <!-- Table -->
        <div class="bg-white rounded-xl overflow-hidden">
          <!-- Loading -->
          <div
            v-if="isLoading"
            class="flex items-center justify-center py-16 text-sm text-[#898790]"
          >
            Lädt...
          </div>

          <!-- Empty -->
          <div
            v-else-if="filtered.length === 0"
            class="flex items-center justify-center py-16 text-sm text-[#898790]"
          >
            Keine Einträge
          </div>

          <!-- Rows -->
          <template v-else>
            <div
              v-for="review in filtered"
              :key="review.id"
              class="border-b border-gray-100 last:border-0"
            >
              <div
                class="flex items-center px-5 py-3.5 gap-4 cursor-pointer hover:bg-gray-50 transition"
                @click="
                  expandedFeedback =
                    expandedFeedback === review.id ? null : review.id
                "
              >
                <!-- Customer -->
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-medium text-gray-900 truncate">
                    {{ review.customer_name || "—" }}
                  </div>
                  <div class="text-xs text-[#898790] truncate mt-0.5">
                    {{ review.project_name || `Projekt #${review.project_id}` }}
                  </div>
                </div>

                <!-- Sent date -->
                <div class="text-xs text-[#898790] w-24 shrink-0">
                  {{ formatDate(review.sent_at) }}
                </div>

                <!-- Status badge -->
                <div class="w-28 shrink-0">
                  <span
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                    :class="{
                      'bg-gray-100 text-gray-500':
                        review.status === 'sent' || review.status === 'pending',
                      'bg-green-50 text-green-700':
                        review.status === 'positive',
                      'bg-red-50 text-red-600': review.status === 'negative',
                    }"
                  >
                    <span
                      class="w-1.5 h-1.5 rounded-full"
                      :class="{
                        'bg-gray-400':
                          review.status === 'sent' ||
                          review.status === 'pending',
                        'bg-green-500': review.status === 'positive',
                        'bg-red-500': review.status === 'negative',
                      }"
                    />
                    {{
                      review.status === "positive"
                        ? "Positiv"
                        : review.status === "negative"
                        ? "Negativ"
                        : "Keine Antwort"
                    }}
                  </span>
                </div>

                <!-- Expand chevron (only for negative with feedback) -->
                <div class="w-4 shrink-0">
                  <svg
                    v-if="review.status === 'negative' && review.feedback_text"
                    class="w-4 h-4 text-gray-400 transition-transform"
                    :class="expandedFeedback === review.id ? 'rotate-180' : ''"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>

              <!-- Feedback text -->
              <div
                v-if="
                  review.status === 'negative' &&
                  review.feedback_text &&
                  expandedFeedback === review.id
                "
                class="px-5 pb-4"
              >
                <div
                  class="bg-red-50 rounded-lg px-4 py-3 text-sm text-gray-700 leading-relaxed"
                >
                  "{{ review.feedback_text }}"
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </MainLayout>
</template>
